import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import AttendanceTable from "./AttendanceTable";
import UserManagePage from "../utils/UserManagePage";
import { API_URL } from '../config';

export default function MainPage({ userId, setUserId, userName, setUserName, isAdmin, setIsAdmin }) {
  const navigate = useNavigate();

  // -------------------------
  // 1. フック（useState）は必ず最初に宣言する
  // -------------------------
  const [userList, setUserList] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // -------------------------
  // 2. 未ログイン弾き処理（最優先）
  // -------------------------
  useEffect(() => {
    // LocalStorage にユーザーIDがなければ、即座にログイン画面へ飛ばす
    const savedUserId = localStorage.getItem("userId");
    if (!savedUserId || userId === null) {
      navigate("/", { replace: true });
    }
  }, [userId, navigate]);

  // -------------------------
  // 3. 初期値の設定（10日締め対応）
  // -------------------------
  useEffect(() => {
    const today = new Date();
    const currentDay = today.getDate(); // 1〜31
    let defaultYear = today.getFullYear();
    let defaultMonth = today.getMonth() + 1;

    // もし今日が「10日以下」なら、前の月をデフォルト表示にする
    if (currentDay <= 10) {
      defaultMonth = defaultMonth - 1;
      // 1月から戻る場合の年またぎ処理（例: 2026年1月5日 → 2025年12月度）
      if (defaultMonth === 0) {
        defaultMonth = 12;
        defaultYear = defaultYear - 1;
      }
    }
    setYear(defaultYear);
    setMonth(defaultMonth);
  }, []);

  // -------------------------
  // 4. 年の選択肢を自動生成 (2025年から来年まで)
  // -------------------------
  const startYear = 2025;
  const currentYearData = new Date().getFullYear();
  const yearOptions = [];
  for (let i = startYear; i <= currentYearData + 1; i++) {
    yearOptions.push(i);
  }

  // -------------------------
  // 5. 管理者用: ユーザー一覧取得
  // -------------------------
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}?action=getUsers`, { credentials: "include" });
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) return;

      const data = await res.json();
      if (data.users) setUserList(data.users);
    } catch (err) {
      console.error("ユーザー一覧取得エラー:", err);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  // -------------------------
  // 6. ログアウト処理
  // -------------------------
  const handleLogout = () => {
    setUserId(null);
    setUserName("ゲスト");
    setIsAdmin(false);
    localStorage.clear();
    
    // 履歴を残さずトップ（ログイン画面）へ
    navigate("/", { replace: true });
  };

  // userIdが決定していない（弾き処理中）の場合は何も描画しない
  if (!userId) return null;

  return (
    <div style={{ padding: "20px", maxWidth: "100%", margin: "0 auto" }}>
      
      {/* ヘッダーエリア */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          {`${userName} さんの勤務表`}
        </h1>
        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <Link to="/password-change" style={{ color: "#007bff", textDecoration: "underline" }}>
            パスワード変更
          </Link>
          <button 
            onClick={handleLogout} 
            style={{ backgroundColor: "#dc3545", color: "white", padding: "6px 12px", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            ログアウト
          </button>
        </div>
      </div>

      {/* 検索・切り替えバー */}
      <div style={{ display: "flex", gap: "15px", alignItems: "center", marginBottom: "20px", padding: "10px", backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "5px" }}>
        
        {/* 管理者用：表示ユーザーの切り替え */}
        {isAdmin && (
          <div style={{ display: "flex", alignItems: "center", gap: "5px", paddingRight: "15px", borderRight: "1px solid #ccc" }}>
            <label style={{ fontWeight: "bold" }}>表示するユーザー:</label>
            <select 
              value={userId} 
              onChange={(e) => setUserId(Number(e.target.value))} 
              style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
            >
              {userList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label style={{ fontWeight: "bold" }}>年:</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}年</option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label style={{ fontWeight: "bold" }}>月:</label>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}月</option>
            ))}
          </select>
        </div>
      </div>

      {/* 勤務表コンポーネント（メインの表） */}
      {(() => {
        const targetUser = userList.find(u => Number(u.id) === Number(userId));
        const displayUserName = (targetUser && targetUser.name) ? targetUser.name : userName;

        return (
          <AttendanceTable 
            year={year} 
            month={month} 
            userId={userId} 
            isAdmin={isAdmin} 
            userName={displayUserName} 
          />
        );
      })()}

      {/* 管理者用：ユーザー管理画面（最下部） */}
      {isAdmin && (
        <div style={{ marginTop: "40px", border: "1px solid #ccc", padding: "20px", borderRadius: "5px", backgroundColor: "#fdfdfd" }}>
          <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "10px", marginTop: 0 }}>
            ユーザー管理（Admin専用）
          </h2>
          <UserManagePage />
        </div>
      )}

    </div>
  );
}